import React, { useCallback, useContext, useMemo } from 'react';
import { QueryClient, UseInfiniteQueryResult, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { AppContext } from 'views/App/context';
import useUrlParams from 'hooks/useUrlParams';
import useLegacyArchivedManuals from 'views/App/Portal/Policy/LegacyArchivedManuals/_data_sources/useLegacyArchivedManuals';
import { ManualItem } from 'views/App/Portal/Policy/_types/manuals/draft';
import { ApiCollectionResponse } from 'types/api_response';
import { ServerErrorResponse } from 'data_sources/constants';

type LegacyArchivedManualsProviderProps = {
  children: React.ReactNode;
};

type LegacyArchivedManualsUrlParams = {
  q?: string | null;
};

export type LegacyArchivedManualsProviderResponse = {
  clearParams: () => void;
  params: LegacyArchivedManualsUrlParams;
  queryClient: QueryClient | null;
  updateParams: (updatedParams: LegacyArchivedManualsUrlParams) => void;
  manuals: ManualItem[];
  manualsQuery: UseInfiniteQueryResult<ApiCollectionResponse<ManualItem>, ServerErrorResponse>;
  manualSchema: any;
  orgId: string;
};

const defaultParams: LegacyArchivedManualsUrlParams = {
  q: null,
};

export const LegacyArchivedManualsContext = React.createContext<LegacyArchivedManualsProviderResponse>(
  {} as LegacyArchivedManualsProviderResponse
);

const LegacyArchivedManualsProvider: ({ children }: LegacyArchivedManualsProviderProps) => any = ({ children }) => {
  const queryClient = useQueryClient();
  const { currentOrganizationId, currentProfile } = useContext(AppContext);
  const orgId = currentOrganizationId;
  const [params, setParams] = useUrlParams(defaultParams);
  const updateParams = useCallback(
    (updatedParams: LegacyArchivedManualsUrlParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const queryParams = useMemo(() => {
    const newParams: any = {
      ...params,
    };
    Object.keys(newParams).forEach((key) => {
      if (newParams[key] === null || newParams[key] === '') {
        delete newParams[key];
      }
    });

    if (currentProfile?.user?.orgId && currentOrganizationId !== currentProfile?.user?.orgId) {
      newParams.orgId = currentOrganizationId;
    }

    return newParams;
  }, [params, currentProfile?.user?.orgId, currentOrganizationId]);

  const manualData = useLegacyArchivedManuals(orgId, queryParams);

  const manualSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required('Name is required'),
  });

  const manuals = useMemo(
    () => (manualData.data && manualData.data.pages ? manualData.data.pages.flatMap((x) => x.content) : []),
    [manualData.data]
  );

  const contextValue: LegacyArchivedManualsProviderResponse = useMemo(
    () => ({
      clearParams,
      params,
      queryClient,
      updateParams,
      manualsQuery: manualData,
      manuals: manuals,
      manualSchema,
      orgId,
    }),
    [clearParams, params, queryClient, updateParams, manualData, manuals, manualSchema, orgId]
  );

  return <LegacyArchivedManualsContext.Provider value={contextValue}>{children}</LegacyArchivedManualsContext.Provider>;
};

export default LegacyArchivedManualsProvider;
